import React from 'react';
import PropTypes from 'prop-types';
import PersonSymbol from '@mui/icons-material/Person';
import { Grid } from '@mui/material';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';
import { DropzoneAreaBase } from 'mui-file-dropzone';
import { get, set } from '@totalsoft/change-tracking-react';
import { dirtyInfo } from 'utils/rules-engine';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import { env } from 'utils/env';
import { useGlobalParams } from 'utils/hooks';

const userAccountUrl = `${env.REACT_APP_IDENTITY_AUTHORITY}/Identity/Account/Manage`;
const userChangePasswordUrl = `${env.REACT_APP_IDENTITY_AUTHORITY}/Identity/Account/Manage/ChangePassword`;

const handleOpenInNewTab = url => event => {
  const win = window.open(url, '_blank');
  win.focus();
  event.preventDefault();
};

export const UserInfoComponent = ({ userInfoLens, toggleExpanded, expanded, onSaveUserInfo, editor, saving, isDirtyInfo }) => {
  const { t } = useTranslation();
  const { enableUserAccountManagement } = useGlobalParams();
  const userInfo = userInfoLens |> get;

  return (
    <CollapseCard
      icon={PersonSymbol}
      title={t('User.UserProfile.UserInfo.Title')}
      expanded={expanded}
      onToggle={toggleExpanded}
      toggleOnHeaderClick
    >
      <Grid container direction='row'>
        <Grid item container xs={12} md={6} xl={3} alignContent='flex-start'>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.FirstName')}:</strong> <span>{userInfo.firstName}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.LastName')}:</strong> <span>{userInfo.lastName}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.MobilePhone')}:</strong> <span>{userInfo.mobilePhone}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.Email')}:</strong> <span>{userInfo.email}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.CompanyName')}:</strong> <span>{userInfo.companyName}</span>
          </Grid>
          <Grid item xs={12}>
            <strong>{t('User.UserProfile.UserInfo.Position')}:</strong> <span>{userInfo.position}</span>
          </Grid>
          {!userInfo.isNotInHierarchy && (
            <>
              <Grid item xs={12}>
                <strong>{t('User.UserProfile.UserInfo.Coordinator')}:</strong> <span>{userInfo.regionalManagerName}</span>
              </Grid>
              {!userInfo.isEzc && !userInfo.isMiddleOffice && (
                <Grid item xs={12}>
                  <strong>{t('User.UserProfile.UserInfo.MiddleOffice')}:</strong> <span>{userInfo.middleOfficeName}</span>
                </Grid>
              )}
              {userInfo.ezcName && !userInfo.isEZC && (
                <Grid item xs={12}>
                  <strong>{t('User.UserProfile.UserInfo.RegionalManager')}:</strong> <span>{userInfo.ezcName}</span>
                </Grid>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          {userInfo.userAdditionalData?.avatar ? (
            <>
              {isDirtyInfo ? (
                <AvatarEditor
                  ref={editor}
                  image={userInfo.userAdditionalData.avatar}
                  width={200}
                  height={193}
                  color={[255, 255, 255, 0.6]} // RGBA
                  scale={1.2}
                  rotate={0}
                  borderRadius={120}
                />
              ) : (
                <img style={{ maxWidth: 250, maxHeigth: 200 }} src={userInfo.userAdditionalData.avatar} alt='profile-pic'></img>
              )}
            </>
          ) : (
            <DropzoneAreaBase
              alignContent='flex-end'
              acceptedFiles={['image/*']}
              dropzoneClass={{ maxWidth: 250, maxHeigth: 200 }}
              filesLimit={1}
              maxFileSize={Number.MAX_VALUE}
              onAdd={files => (userInfoLens.userAdditionalData.avatar |> set)(files[0].data)}
              onDrag={files => (userInfoLens.userAdditionalData.avatar |> set)(files[0].data)}
            />
          )}
        </Grid>
        <Grid container justifyContent='flex-start' spacing={1} gap={1} mt={1}>
          {enableUserAccountManagement && (
            <>
              <Button variant='contained' onClick={handleOpenInNewTab(userAccountUrl)}>
                {t('User.UserProfile.MyAccount')}
              </Button>
              <Button variant='contained' onClick={handleOpenInNewTab(userChangePasswordUrl)}>
                {t('User.UserProfile.ChangePassword')}
              </Button>
            </>
          )}
          <Button
            variant='outlined'
            onClick={() => (userInfoLens.userAdditionalData.avatar |> set)(null)}
            disabled={!userInfo.userAdditionalData.avatar || !dirtyInfo || saving}
          >
            {t('User.UserProfile.UserInfo.ClearPicture')}
          </Button>
          <Button variant='contained' onClick={() => onSaveUserInfo(editor)} disabled={saving || !isDirtyInfo}>
            {saving ? t('Saving') : t('Save')}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

UserInfoComponent.propTypes = {
  userInfoLens: PropTypes.object.isRequired,
  toggleExpanded: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onSaveUserInfo: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  isDirtyInfo: PropTypes.bool.isRequired,
  editor: PropTypes.object
};

export default UserInfoComponent;
